/**
 * Template file-tree helpers for the Templates gallery CLI.
 *
 * A template's pull/push diff is a FILE-TREE + metadata diff (not the entity-aware
 * hub diff engine) — so these are plain readers/writers over a `{ path: body }` map.
 * Text-only for now (machinery v1); binary resource files inside a slice are a
 * documented follow-up.
 */

import * as fs from 'node:fs';
import * as path from 'node:path';
import { isSafeTemplatePath } from '@wayai/core/contracts';
import { cleanOrphanFiles } from './resource-files.js';

/** Recursively read a directory tree into a flat `{ relativePath: utf8Body }` map. */
export function readDirToFileMap(rootDir: string): Record<string, string> {
  const out: Record<string, string> = {};
  function walk(dir: string, prefix: string): void {
    const entries = fs.readdirSync(dir, { withFileTypes: true }).sort((a, b) => a.name.localeCompare(b.name));
    for (const entry of entries) {
      const abs = path.join(dir, entry.name);
      const rel = prefix ? `${prefix}/${entry.name}` : entry.name;
      if (entry.isDirectory()) walk(abs, rel);
      else if (entry.isFile()) out[rel] = fs.readFileSync(abs, 'utf-8');
    }
  }
  if (fs.existsSync(rootDir)) walk(rootDir, '');
  return out;
}

/**
 * Return the subset of `files` keys whose target already exists under `targetDir`
 * (i.e. would be truncate-overwritten by `writeFileMap`). Sorted for stable output.
 * Validates each path (defense-in-depth) — this is the first path-traversal sink in
 * a pull, running before `writeFileMap`. Used by callers to refuse a clobbering pull.
 */
export function detectCollisions(targetDir: string, files: Record<string, string>): string[] {
  const collisions: string[] = [];
  for (const rel of Object.keys(files)) {
    if (!isSafeTemplatePath(rel)) {
      throw new Error(`Refusing to inspect unsafe template path: ${rel}`);
    }
    if (fs.existsSync(path.join(targetDir, rel))) collisions.push(rel);
  }
  return collisions.sort((a, b) => a.localeCompare(b));
}

/**
 * Write a `{ relativePath: body }` map under `targetDir`, creating parent dirs.
 * Re-validates each path (defense-in-depth) — the server already constrained the
 * keys via the same `isSafeTemplatePath`, but the writer is a path-traversal sink.
 * Returns the written relative paths.
 */
export function writeFileMap(targetDir: string, files: Record<string, string>): string[] {
  const written: string[] = [];
  for (const [rel, body] of Object.entries(files)) {
    if (!isSafeTemplatePath(rel)) {
      throw new Error(`Refusing to write unsafe template path: ${rel}`);
    }
    const abs = path.join(targetDir, rel);
    fs.mkdirSync(path.dirname(abs), { recursive: true });
    fs.writeFileSync(abs, body, 'utf-8');
    written.push(rel);
  }
  return written;
}

/** Relative paths of every file under `dir` (path-only walk — no file bodies read). */
export function listDirRelFiles(dir: string): string[] {
  const out: string[] = [];
  function walk(d: string, prefix: string): void {
    for (const entry of fs.readdirSync(d, { withFileTypes: true })) {
      const rel = prefix ? `${prefix}/${entry.name}` : entry.name;
      if (entry.isDirectory()) walk(path.join(d, entry.name), rel);
      else if (entry.isFile()) out.push(rel);
    }
  }
  if (fs.existsSync(dir)) walk(dir, '');
  return out;
}

/**
 * Make `targetDir` EQUAL `files` (a clean snapshot): write every entry, then remove any
 * file already under `targetDir` that isn't in the new map and prune the empty dirs it
 * leaves behind. Used by `wayai admin template freeze` (gh #2960) — a re-freeze must drop
 * an agent/eval that was deleted from the live hub, not leave it stranded in the slice.
 * The remove + empty-dir prune reuses `cleanOrphanFiles` (one pass, path-only, traversal-safe
 * over walk-discovered entries). Returns the written + removed relative paths (sorted).
 */
export function writeSliceSnapshot(
  targetDir: string,
  files: Record<string, string>,
): { written: string[]; removed: string[] } {
  const keep = new Set(Object.keys(files));
  const removed = listDirRelFiles(targetDir)
    .filter((rel) => !keep.has(rel))
    .sort((a, b) => a.localeCompare(b));
  const written = writeFileMap(targetDir, files).sort((a, b) => a.localeCompare(b));
  // Reconcile whenever the dir exists (post-write it does, unless `files` was empty) so the
  // snapshot also prunes an empty dir a prior freeze left behind — not only when files were
  // orphaned this run. `cleanOrphanFiles` is a no-op when nothing is stale.
  if (fs.existsSync(targetDir)) cleanOrphanFiles(targetDir, '', keep);
  return { written, removed };
}
